/**
 * GuestServicesActivitiesPanel — a guest's unified activities + services, with
 * per-item actions. Reusable: embedded in the guest profile (guest known) and
 * inside the frontdesk person modal. Reads the unified `guest-schedule`
 * aggregate; cancels are kind-aware (service booking vs activity enrollment).
 *
 * Layout follows the "Guest · services & activities" mockup (Guest 360):
 * summary strip → filter chips (incl. today / unsettled / cancelled) →
 * day-grouped rows with a type-colored icon tile, status pill, charge state
 * and a contextual inline action. Cancelled rows render dimmed.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from '@/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/theme/components/ui/card';
import { Button } from '@/theme/components/ui/button';
import { ConfirmDialog, LoadingState } from '@/shared/components';
import { useFormattedDate, useLocationCurrency } from '@/shared/hooks';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/theme/components/ui/dropdown-menu';
import {
  CalendarClock, MoreHorizontal, Plus, Check, CalendarSync, X, List, CalendarDays, ChevronDown,
} from 'lucide-react';
import { useGuestSchedule, useCancelServiceBooking, useCheckInServiceBooking } from '../hooks/useServiceBookings';
import { useCancelEnrollment } from '@/modules/activities/hooks/useEnrollments';
import { useActivities } from '@/modules/activities/hooks/useActivities';
import { EnrollGuestDialog } from '@/modules/activities/components/EnrollGuestDialog';
import { typeVisual } from '../utils/typeVisuals';
import { AddServiceToGuestDialog } from './AddServiceToGuestDialog';
import { MoveServiceBookingDialog } from './MoveServiceBookingDialog';
import { GuestScheduleCalendar } from './GuestScheduleCalendar';
import type { GuestScheduleItem } from '../types';

type Filter = 'all' | 'activity' | 'service' | 'today' | 'unsettled' | 'cancelled';
type View = 'list' | 'calendar';

interface Props {
  guestId: string;
  /** The guest's booking + stay window — enables checkout settlement and seeds the add flow. */
  bookingId?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  /** Guest display name — used when enrolling into an activity. */
  guestName?: string | null;
  /** Optional date window; omit for the whole schedule. */
  dateFrom?: string;
  dateTo?: string;
}

/** Status pill colors, keyed loosely — enrollment and booking statuses both land here. */
const STATUS_PILL: Record<string, string> = {
  booked: 'bg-ocean-100 text-ocean-800',
  enrolled: 'bg-ocean-100 text-ocean-800',
  confirmed: 'bg-ocean-100 text-ocean-800',
  checked_in: 'bg-sage-100 text-sage-800',
  attended: 'bg-sage-100 text-sage-800',
  completed: 'bg-sand-100 text-sand-700',
  waitlisted: 'bg-sun-100 text-sun-800',
  no_show: 'bg-coral-100 text-coral-800',
  cancelled: 'bg-coral-100 text-coral-800',
};

const CANCELLED_STATUSES = new Set(['cancelled']);

function localTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function GuestServicesActivitiesPanel({ guestId, bookingId, checkIn, checkOut, guestName, dateFrom, dateTo }: Props) {
  const { t } = useTranslation();
  const { format } = useFormattedDate();
  const { formatPrice } = useLocationCurrency();
  const [filter, setFilter] = useState<Filter>('all');
  const [view, setView] = useState<View>('list');
  const [toCancel, setToCancel] = useState<GuestScheduleItem | null>(null);
  const [toMove, setToMove] = useState<GuestScheduleItem | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addActivityOpen, setAddActivityOpen] = useState(false);

  const { data: items = [], isLoading } = useGuestSchedule(guestId, dateFrom, dateTo);

  // Activity sessions to enroll into — scheduled sessions in the guest's stay
  // window (falls back to the next 30 days). Fetched only once the guest opens
  // the activity picker. Location scope comes from the URL via apiClient.
  const enrollWindowFrom = checkIn ?? localTodayISO();
  const enrollWindowTo = useMemo(() => {
    if (checkOut) return checkOut;
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [checkOut]);
  const { data: activityPool = [] } = useActivities(
    { dateFrom: enrollWindowFrom, dateTo: enrollWindowTo, status: 'scheduled' },
    { enabled: addActivityOpen },
  );
  const cancelService = useCancelServiceBooking();
  const cancelEnrollment = useCancelEnrollment();
  const checkInBooking = useCheckInServiceBooking();

  const today = localTodayISO();
  const matches = useMemo(() => ({
    all: () => true,
    activity: (i: GuestScheduleItem) => i.kind === 'activity',
    service: (i: GuestScheduleItem) => i.kind === 'service',
    today: (i: GuestScheduleItem) => i.date === today && !CANCELLED_STATUSES.has(i.status),
    unsettled: (i: GuestScheduleItem) => i.chargeStatus === 'open',
    cancelled: (i: GuestScheduleItem) => CANCELLED_STATUSES.has(i.status),
  }), [today]);

  const filtered = useMemo(() => items.filter(matches[filter]), [items, matches, filter]);

  const groups = useMemo(() => {
    const map = new Map<string, GuestScheduleItem[]>();
    for (const it of filtered) {
      const key = it.date ?? '—';
      (map.get(key) ?? map.set(key, []).get(key)!).push(it);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const counts = useMemo(() => (
    Object.fromEntries(
      (Object.keys(matches) as Filter[]).map((k) => [k, items.filter(matches[k]).length]),
    ) as Record<Filter, number>
  ), [items, matches]);

  const unsettledTotal = useMemo(
    () => items.filter(matches.unsettled).reduce((sum, i) => sum + Number(i.priceCharged), 0),
    [items, matches],
  );

  const confirmCancel = () => {
    if (!toCancel) return;
    const done = () => setToCancel(null);
    if (toCancel.kind === 'service') cancelService.mutate(toCancel.id, { onSettled: done });
    else cancelEnrollment.mutate({ id: toCancel.id }, { onSettled: done });
  };

  const chip = (key: Filter, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => setFilter(key)}
      className={
        filter === key
          ? 'rounded-full border border-ocean-700 bg-ocean-700 px-3 py-1 text-[12px] font-medium text-white'
          : 'rounded-full border border-sand-200 bg-card px-3 py-1 text-[12px] font-medium text-sand-900 hover:border-sand-400'
      }
    >
      {label} <span className="font-mono text-[10px] opacity-70">{counts[key]}</span>
    </button>
  );

  const settleLabel = (it: GuestScheduleItem): { text: string; cls: string } => {
    if (it.chargeStatus === 'open') {
      return { text: t('services.guestPanel.charge.open', 'Unsettled'), cls: 'text-coral-700' };
    }
    if (it.chargeStatus === 'settled') {
      return { text: t('services.guestPanel.charge.settled', 'Paid'), cls: 'text-sage-700' };
    }
    if (it.chargeStatus === 'voided') {
      return { text: t('services.guestPanel.charge.voided', 'Voided'), cls: 'text-muted-foreground' };
    }
    if (Number(it.priceCharged) === 0) {
      return { text: t('services.guestPanel.charge.included', 'Included'), cls: 'text-sage-700' };
    }
    return { text: '—', cls: 'text-muted-foreground' };
  };

  const whenLine = (it: GuestScheduleItem): string => {
    const time = it.startTime
      ? it.startTime.slice(0, 5) + (it.endTime ? `–${it.endTime.slice(0, 5)}` : '')
      : t('services.guestPanel.noTime', 'no time');
    return it.route ? `${time} · ${it.route}` : time;
  };

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <CalendarClock className="h-4 w-4" />
          {t('services.guestPanel.title', 'Activities & services')}
        </CardTitle>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center rounded-full border border-sand-200 p-0.5">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`rounded-full p-1 ${view === 'list' ? 'bg-ocean-700 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label={t('services.guestPanel.viewList', 'List')}
              aria-pressed={view === 'list'}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setView('calendar')}
              className={`rounded-full p-1 ${view === 'calendar' ? 'bg-ocean-700 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label={t('services.guestPanel.viewCalendar', 'Calendar')}
              aria-pressed={view === 'calendar'}
            >
              <CalendarDays className="h-3.5 w-3.5" />
            </button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-7 gap-1 px-2.5">
                <Plus className="h-3.5 w-3.5" />
                {t('services.add.button', 'Add')}
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setAddActivityOpen(true)}>
                {t('services.add.activity', 'Add activity')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddOpen(true)}>
                {t('services.add.service', 'Add service')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* summary strip */}
        <div className="grid grid-cols-3 gap-2.5">
          <div className="rounded-lg border border-border bg-card px-3.5 py-2.5">
            <p className="font-mono text-lg font-semibold">{counts.activity}</p>
            <p className="text-[10.5px] uppercase tracking-[0.05em] text-muted-foreground">
              {t('services.guestPanel.activities', 'Activities')}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card px-3.5 py-2.5">
            <p className="font-mono text-lg font-semibold">{counts.service}</p>
            <p className="text-[10.5px] uppercase tracking-[0.05em] text-muted-foreground">
              {t('services.guestPanel.services', 'Services')}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card px-3.5 py-2.5">
            <p className="font-mono text-lg font-semibold text-coral-700">{formatPrice(unsettledTotal)}</p>
            <p className="text-[10.5px] uppercase tracking-[0.05em] text-muted-foreground">
              {t('services.guestPanel.unsettled', 'Unsettled')}
            </p>
          </div>
        </div>

        {/* filter chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {chip('all', t('common.all', 'All'))}
          {chip('activity', t('services.guestPanel.activities', 'Activities'))}
          {chip('service', t('services.guestPanel.services', 'Services'))}
          {chip('today', t('services.guestPanel.today', 'Today'))}
          {chip('unsettled', t('services.guestPanel.unsettled', 'Unsettled'))}
          {chip('cancelled', t('services.guestPanel.cancelledChip', 'Cancelled'))}
          <span className="ml-auto text-[11.5px] text-muted-foreground">
            {t('services.guestPanel.showing', 'Showing {{shown}} of {{total}}', {
              shown: filtered.length,
              total: items.length,
            })}
          </span>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('services.guestPanel.empty', 'No activities or services yet.')}
          </p>
        ) : view === 'calendar' ? (
          <GuestScheduleCalendar items={filtered.filter((i) => !CANCELLED_STATUSES.has(i.status))} />
        ) : (
          groups.map(([day, rows]) => (
            <div key={day} className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {day === '—' ? t('services.guestPanel.anytime', 'Anytime') : format(day, 'SHORT_DATE')}
              </p>
              <div className="divide-y divide-border rounded-lg border border-border">
                {rows.map((it) => {
                  const cancelled = CANCELLED_STATUSES.has(it.status);
                  const { icon: TileIcon, tile } = typeVisual(it.kind === 'activity' ? 'activity' : it.serviceType);
                  const settle = settleLabel(it);
                  const canCheckIn = !cancelled && it.kind === 'service' && it.status === 'booked';
                  return (
                    <div key={`${it.kind}-${it.id}`} className={`flex items-center gap-3 p-3 ${cancelled ? 'opacity-55' : ''}`}>
                      <div className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg ${tile}`}>
                        <TileIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-sm font-medium">
                          <span className="truncate">{it.title}</span>
                          <span
                            className={`rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em] ${
                              it.kind === 'activity' ? 'bg-ocean-100 text-ocean-800' : 'bg-coral-100 text-coral-800'
                            }`}
                          >
                            {t(`services.guestPanel.kind.${it.kind}`, it.kind)}
                          </span>
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{whenLine(it)}</p>
                      </div>
                      <span
                        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] ${
                          STATUS_PILL[it.status] ?? 'bg-sand-100 text-sand-700'
                        }`}
                      >
                        {t(`services.guestPanel.status.${it.status}`, it.status)}
                      </span>
                      <div className="min-w-[68px] text-right">
                        <p className="font-mono text-sm font-semibold">{formatPrice(Number(it.priceCharged))}</p>
                        <p className={`text-[10px] font-semibold ${settle.cls}`}>{settle.text}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {canCheckIn && (
                          <Button
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                            onClick={() => checkInBooking.mutate(it.id)}
                            disabled={checkInBooking.isPending}
                          >
                            {t('services.guestPanel.checkIn', 'Check in')}
                          </Button>
                        )}
                        {!cancelled && it.status !== 'completed' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={t('common.actions', 'Actions')}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canCheckIn && (
                                <DropdownMenuItem onClick={() => checkInBooking.mutate(it.id)}>
                                  <Check className="mr-2 h-4 w-4" />
                                  {t('services.guestPanel.checkIn', 'Check in')}
                                </DropdownMenuItem>
                              )}
                              {it.kind === 'service' && (
                                <DropdownMenuItem onClick={() => setToMove(it)}>
                                  <CalendarSync className="mr-2 h-4 w-4" />
                                  {t('services.guestPanel.reschedule', 'Reschedule')}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem className="text-destructive" onClick={() => setToCancel(it)}>
                                <X className="mr-2 h-4 w-4" />
                                {t('common.cancel', 'Cancel')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </CardContent>

      <ConfirmDialog
        open={!!toCancel}
        onOpenChange={() => setToCancel(null)}
        title={t('services.guestPanel.cancelTitle', 'Cancel this item')}
        description={t('services.guestPanel.cancelDescription', 'Cancel "{{title}}"? Any open charge is voided. This cannot be undone.', { title: toCancel?.title })}
        confirmLabel={t('common.confirm', 'Confirm')}
        onConfirm={confirmCancel}
        isLoading={cancelService.isPending || cancelEnrollment.isPending}
      />

      <AddServiceToGuestDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        guestId={guestId}
        bookingId={bookingId}
        checkIn={checkIn}
        checkOut={checkOut}
        schedule={items}
      />

      <EnrollGuestDialog
        open={addActivityOpen}
        onOpenChange={setAddActivityOpen}
        activities={activityPool}
        inHouseGuests={[{ guestId, personId: null, name: guestName ?? '', bookingId: bookingId ?? null }]}
        staff={[]}
        initialGuestId={guestId}
        initialGuestName={guestName ?? null}
        initialBookingId={bookingId ?? null}
      />

      {toMove && (
        <MoveServiceBookingDialog
          open={!!toMove}
          onOpenChange={(o) => { if (!o) setToMove(null); }}
          bookingId={toMove.id}
          schedule={items}
        />
      )}
    </Card>
  );
}
